import { Box, Flex } from "@chakra-ui/react";
import { useField } from "formik";
import { SelectControl } from "formik-chakra-ui";
import React from "react";
import { HighlightedText } from "src/components/Text/HighlightedText";
import {
  WEGLOT_APPLY_CLASS,
  WEGLOT_SKIP_CLASS,
} from "src/plugins/weglot/constants";
import { FormikFieldValue } from "src/services/formTemplate/answer";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";
import { FormikSearchSelect } from "../SearchSelect";
import { BaseInputProps, Option } from "./Question";
import { WithTranslation } from "./WithTranslation";

const SELECTED_ARCHIVED_STYLE = {
  fontStyle: "italic",
  color: "var(--chakra-colors-gray-500)",
  opacity: 0.7,
};

export interface SingleSelectProps extends BaseInputProps {
  isRequired?: boolean;
  options: Option[];
  onOptionSelected?: (option: Option) => void;
  onSelectionClear?: () => void;
}

export const SingleSelectInput: React.FC<SingleSelectProps> = ({
  id,
  options,
  onOptionSelected,
  onSelectionClear,
  isDisabled = false,
  isRequired = false,
}) => {
  const [field, , helpers] = useField<FormikFieldValue>(id);

  const value = field.value;

  // Find the selected option to check if it's archived
  const selectedOption = React.useMemo(() => {
    return options.find((o) => o.key === value);
  }, [options, value]);
  const isSelectedArchived = selectedOption?.isArchived ?? false;

  // Filter out archived options from dropdown, but keep the selected one if it's archived
  const availableOptions = React.useMemo(() => {
    // Always filter out archived options, except if one is currently selected
    return options.filter((o) => !o.isArchived || o.key === value);
  }, [options, value]);

  React.useEffect(() => {
    if (!onOptionSelected) return;
    for (const option of options) {
      if (option.key === value) {
        onOptionSelected(option);
        continue;
      }
    }
    if (value === "" && onSelectionClear) {
      onSelectionClear();
    }
  }, [value, options, onOptionSelected, onSelectionClear]);

  const items = React.useMemo(
    () =>
      availableOptions.map((o) => {
        const label = o.label ?? o.key;
        return {
          key: o.key,
          label,
          disabledLabel: o.isArchived ? "(archived)" : null,
          translate: o.translate,
          disabled: o.isArchived,
        };
      }),
    [availableOptions]
  );

  if (availableOptions.length > 8) {
    return (
      <WithTranslation items={items}>
        {(translatedItems) => (
          <FormikSearchSelect
            name={id}
            label=""
            disabled={isDisabled}
            placeholder="Select answer"
            formControlProps={{ isRequired }}
            variant="unlocked"
            items={translatedItems}
            inputProps={{
              ...PASSWORD_MANAGER_IGNORE,
              /** We need to manually override aria-labelledby and id since we're not using the built in label provided by this component.
               * Instead, it will rely on `for` attribute on the label to match the id here.
               */
              "aria-labelledby": undefined,
              id,
            }}
            renderItem={(item, searchValue) => (
              <Flex gap="2" justifyContent="space-between" alignItems="center">
                <Box whiteSpace="pre-line" className={WEGLOT_SKIP_CLASS}>
                  <HighlightedText
                    text={item.label}
                    keyword={searchValue}
                    invert
                  />
                </Box>
              </Flex>
            )}
          />
        )}
      </WithTranslation>
    );
  }

  return (
    <>
      <SelectControl
        isDisabled={isDisabled}
        name={id}
        selectProps={{
          placeholder: "Select answer",
          style: isSelectedArchived ? SELECTED_ARCHIVED_STYLE : undefined,
        }}
        isRequired={isRequired}
        onChange={(event) => {
          // set as touched so it triggers autosave on change instead of on blur.
          helpers.setTouched(true);
          field.onChange(event);
        }}
      >
        {availableOptions.map((o) => {
          const label = o.label ?? o.key;
          return (
            <option
              className={o.translate ? WEGLOT_APPLY_CLASS : WEGLOT_SKIP_CLASS}
              value={o.key}
              key={o.key}
              disabled={o.isArchived}
              style={isSelectedArchived ? SELECTED_ARCHIVED_STYLE : undefined}
            >
              {o.isArchived ? `${label} (archived)` : label}
            </option>
          );
        })}
      </SelectControl>
    </>
  );
};
